import * as React from 'react';
import {Mutation} from 'react-apollo';

import type {History} from 'history';

import {IntegrationWidget} from 'shells/integration-widget/integration-widget';
import GoogleLogoSvg from 'shells/logos/google-g.svg';
import type {LocationEnum} from 'shells/popover/popover';

import type {
    setGoogleCalendarSyncIsEnabled as setGoogleCalendarSyncIsEnabledMutation,
    setGoogleCalendarSyncIsEnabledVariables as setGoogleCalendarSyncIsEnabledMutationVariables,
    GetCalendars_calendars as Calendar,
} from 'nutshell-graphql-types';

import {authToService} from '../integrations/auth-to-service';
import {ClearIntegration} from '../integrations/clear-integration';
import {getAuthUrl} from '../integrations/integration-mutation-response-helpers';

import {useSelectCalendarSyncCalendar} from './graphql/hooks';
import SET_GOOGLE_CALENDAR_SYNC_IS_ENABLED from './graphql/mutations/set-google-calendar-sync-is-enabled.graphql';
import {SelectCalendarModalWrapper} from './select-calendar-modal/select-calendar-modal-wrapper';
import {updateCacheFromMutation} from './update-cache-from-mutation';

export const googleLogo = <GoogleLogoSvg width={24} />;
const windowFeatures = {
    toolbar: 'no',
    location: 'no',
    width: 600,
    height: 850,
} as const;

class GoogleEnableMutation extends Mutation<
    setGoogleCalendarSyncIsEnabledMutation,
    setGoogleCalendarSyncIsEnabledMutationVariables
> {}

type Props = {
    username: string;
    isEnabled: boolean;
    onAuthStart: () => void;
    onAuthSuccess: () => Promise<any>;
    onAuthError: (arg1: string[]) => void;
    externalService: 'GOOGLE' | 'GOOGLE_APPS';
    errors?: string[];
    statusMessage?: string;
    onlyDisable?: boolean;
    dialogButtonText?: string;
    revokeWarning?: string;
    location?: LocationEnum;
    onRevokeClick?: () => void;
    primaryCalendar?: Calendar;
    appHistory: History;
    isEmbedded?: boolean;
};

export function CalendarWidgetGoogle(props: Props) {
    const [isAuthenticating, setIsAuthenticating] = React.useState<boolean>(false);
    const [dialogButtonText, setDialogButtonText] = React.useState<string>('Disconnect');
    const [isChangingCalendar, setIsChangingCalendar] = React.useState(false);

    const {selectCalendarSyncCalendar} = useSelectCalendarSyncCalendar();

    return (
        <>
            <ClearIntegration>
                {({clearIntegration}) => (
                    <GoogleEnableMutation
                        mutation={SET_GOOGLE_CALENDAR_SYNC_IS_ENABLED}
                        update={updateCacheFromMutation}
                    >
                        {(enableGoogle, {loading: isMutationInFlight}) => {
                            const hasErrors = props.errors && props.errors.length > 0;
                            let status = props.isEnabled && !hasErrors ? 'enabled' : 'disabled';
                            if (isMutationInFlight || isAuthenticating) {
                                status = props.isEnabled ? 'disabling' : 'enabling';
                            }

                            const statusMessage = hasErrors ? 'Needs attention' : undefined;

                            return (
                                <IntegrationWidget
                                    // @ts-expect-error
                                    status={status}
                                    account={props.username}
                                    errors={props.errors}
                                    statusMessage={
                                        statusMessage ? statusMessage : props.statusMessage
                                    }
                                    onlyDisable={props.onlyDisable}
                                    hideForgetButton={props.externalService === 'GOOGLE_APPS'}
                                    dialogButtonText={
                                        props.dialogButtonText
                                            ? props.dialogButtonText
                                            : dialogButtonText
                                    }
                                    logo={googleLogo}
                                    onConnectClick={() => {
                                        enableGoogle({variables: {isEnabled: true}})
                                            .then((mutationResult) => {
                                                const authUrl = getAuthUrl(mutationResult);
                                                if (authUrl) {
                                                    return authUrl;
                                                } else if (
                                                    !props.isEnabled &&
                                                    !props.primaryCalendar
                                                ) {
                                                    setIsChangingCalendar(true);

                                                    return null;
                                                }
                                            })
                                            .then((authUrl) => {
                                                // If we get an authUrl back, then we need to auth
                                                // Highly unlikely in the case of google,
                                                // because we get all scopes up front.
                                                if (authUrl) {
                                                    props.onAuthStart();
                                                    setIsAuthenticating(true);
                                                    authToService({
                                                        authUrl,
                                                        windowFeatures,
                                                        authCheckUrl: '/google/has-auth',
                                                    })
                                                        .then(({hasAuth}) => {
                                                            if (hasAuth) {
                                                                props.onAuthSuccess().then(() => {
                                                                    setIsAuthenticating(false);
                                                                });
                                                            }
                                                        })
                                                        .catch((errMsg) => {
                                                            props.onAuthError(errMsg);
                                                            setIsAuthenticating(false);
                                                        });
                                                }
                                            });
                                    }}
                                    onDisconnectClick={() => {
                                        enableGoogle({variables: {isEnabled: false}});
                                    }}
                                    revokeWarning={
                                        props.revokeWarning
                                            ? props.revokeWarning
                                            : 'This will also remove this account from email and task sync.'
                                    }
                                    revokeWarningLocation={props.location}
                                    onRevokeClick={() => {
                                        if (props.onRevokeClick) {
                                            props.onRevokeClick();
                                        } else if (props.externalService !== 'GOOGLE_APPS') {
                                            props.onAuthStart();
                                            clearIntegration('GOOGLE')
                                                // TODO: does this actually work?
                                                .catch((errMsg) => {
                                                    props.onAuthError(errMsg);
                                                });
                                            setDialogButtonText('Disconnecting...');
                                        }
                                    }}
                                />
                            );
                        }}
                    </GoogleEnableMutation>
                )}
            </ClearIntegration>
            {isChangingCalendar && (
                <SelectCalendarModalWrapper
                    username={props.username}
                    isOpen={isChangingCalendar}
                    onClose={() => setIsChangingCalendar(false)}
                    onSave={(calendarId) => {
                        return selectCalendarSyncCalendar(calendarId);
                    }}
                    modalType='sync'
                    isEmbedded={props.isEmbedded}
                />
            )}
        </>
    );
}
